import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

export interface GeoPoint {
  lat: number;
  lng: number;
}

/**
 * 위치 감시 (§5.20 P6 버스 위치 송출) — 플랫폼별 권한 처리를 한 곳에 모은다.
 *
 * 앱(Capacitor 웹뷰)의 navigator.geolocation은 안드로이드 시스템 권한 요청으로
 * 이어지지 않아 항상 실패한다. 네이티브에서는 Geolocation 플러그인으로
 * 권한 팝업을 먼저 띄우고 GPS를 읽는다. 브라우저에서는 표준 API를 쓴다.
 *
 * 반환값은 감시 해제 함수 — useEffect cleanup에서 부른다.
 */
export interface WatchOptions {
  /**
   * 정밀 위치 여부. 버스 송출은 true(GPS), 등하원 위치 공유(§5.10 v1.22)는 false —
   * 서버가 어차피 약 100m로 반올림하므로 정밀 위치를 읽을 이유가 없고 배터리도 아낀다.
   */
  highAccuracy?: boolean;
}

export async function watchLocation(
  onPoint: (point: GeoPoint) => void,
  onError: (message: string) => void,
  options: WatchOptions = {},
): Promise<() => void> {
  const enableHighAccuracy = options.highAccuracy ?? true;
  if (Capacitor.isNativePlatform()) {
    const permission = await Geolocation.requestPermissions().catch(() => null);
    const granted =
      permission?.location === 'granted' || permission?.coarseLocation === 'granted';
    if (!granted) {
      onError('위치 권한이 필요해요. 앱 설정에서 위치를 허용해 주세요.');
      return () => {};
    }

    const id = await Geolocation.watchPosition(
      { enableHighAccuracy, maximumAge: enableHighAccuracy ? 5_000 : 15_000 },
      (position, error) => {
        if (error || !position) {
          onError('위치를 잠시 읽지 못했어요. 자동으로 다시 시도해요.');
          return;
        }
        onPoint({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
    );
    return () => void Geolocation.clearWatch({ id });
  }

  if (!('geolocation' in navigator)) {
    onError('이 기기에서는 위치를 쓸 수 없어요.');
    return () => {};
  }
  const watchId = navigator.geolocation.watchPosition(
    (position) => onPoint({ lat: position.coords.latitude, lng: position.coords.longitude }),
    () => onError('위치 권한이 없어 공유할 수 없어요. 브라우저 설정을 확인해 주세요.'),
    { enableHighAccuracy, maximumAge: enableHighAccuracy ? 5_000 : 15_000 },
  );
  return () => navigator.geolocation.clearWatch(watchId);
}

/**
 * 한 번만 읽기 — 출발 버튼·원 위치 설정처럼 첫 좌표가 필요할 때.
 * 권한이 없거나 못 읽으면 메시지를 담아 던진다(호출부가 화면에 그대로 보여 준다).
 */
export async function readLocationOnce(options: WatchOptions = {}): Promise<GeoPoint> {
  const enableHighAccuracy = options.highAccuracy ?? false;

  if (Capacitor.isNativePlatform()) {
    const permission = await Geolocation.requestPermissions().catch(() => null);
    const granted =
      permission?.location === 'granted' || permission?.coarseLocation === 'granted';
    if (!granted) throw new Error('위치 권한이 필요해요. 앱 설정에서 위치를 허용해 주세요.');
    const position = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout: 15_000 });
    return { lat: position.coords.latitude, lng: position.coords.longitude };
  }

  if (!('geolocation' in navigator)) throw new Error('이 기기에서는 위치를 쓸 수 없어요.');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => reject(new Error('위치 권한이 없어 읽을 수 없어요. 브라우저 설정을 확인해 주세요.')),
      { enableHighAccuracy, timeout: 15_000, maximumAge: 30_000 },
    );
  });
}
